import { ActionIcon, Card, Collapse, Group, Text } from '@mantine/core';
import {
    IconChevronDown,
    IconChevronRight,
    IconDots,
    IconPencil,
    IconPlus,
    IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useState } from 'react';
import { isString } from '../../utils/utilFunctions';

export const CollapsibleSection = ({
    label,
    labelBarNode,
    handleExpansionChange,
    disabled,
    initialOpen = false,
    withoutBackgroundColor,
    handleEditClick,
    handleDeleteClick,
    handleAddClick,
    handleMoreClick,
    children,
    disableAdd,
}: {
    children: ReactNode | Array<ReactNode>;
    initialOpen?: boolean;
    disabled?: boolean;
    label?: ReactNode;
    labelBarNode?: ReactNode;
    withoutBackgroundColor?: boolean;
    handleEditClick?: () => void;
    handleMoreClick?: () => void;
    handleAddClick?: () => void;
    handleDeleteClick?: () => void;
    handleExpansionChange?: (value: boolean) => void;
    disableAdd?: boolean;
}) => {
    const [open, setOpen] = useState(initialOpen);

    const getEditButton = () => {
        if (handleEditClick) {
            return (
                <ActionIcon
                    size={'sm'}
                    onClick={handleEditClick}
                    variant={'transparent'}
                    color={'var(--mantine-color-base-button)'}
                >
                    <IconPencil />
                </ActionIcon>
            );
        }
        return;
    };

    const getDeleteButton = () => {
        if (handleDeleteClick) {
            return (
                <ActionIcon
                    size={'sm'}
                    onClick={handleDeleteClick}
                    variant={'transparent'}
                    color={'var(--mantine-color-base-button)'}
                >
                    <IconTrash />
                </ActionIcon>
            );
        }
        return;
    };

    const getAddButton = () => {
        if (handleAddClick) {
            return (
                <ActionIcon
                    size={'sm'}
                    onClick={handleAddClick}
                    variant={'transparent'}
                    color={'var(--mantine-color-base-button)'}
                    disabled={disableAdd}
                >
                    <IconPlus />
                </ActionIcon>
            );
        }
        return;
    };

    const getMoreButton = () => {
        if (handleMoreClick) {
            return (
                <ActionIcon
                    size={'sm'}
                    onClick={handleMoreClick}
                    variant={'transparent'}
                    color={'var(--mantine-color-base-button)'}
                >
                    <IconDots />
                </ActionIcon>
            );
        }
        return;
    };

    return (
        <Card
            mih={32}
            withBorder
            padding={'sm'}
            style={{
                display: 'flex',
                justifyContent: 'space-between',
                flexDirection: 'column',
                backgroundColor:
                    withoutBackgroundColor ? undefined
                    : (
                        Boolean(handleEditClick) ||
                        Boolean(handleAddClick) ||
                        disabled
                    ) ?
                        'var(--mantine-color-read-only-input)'
                    :   undefined,
            }}
        >
            <Card.Section
                mih={32}
                style={{
                    padding: 10,
                    paddingTop: 2,
                    paddingBottom: open && children ? 10 : 0,
                }}
            >
                <div
                    style={{
                        width: '100%',
                        display: 'inline-flex',
                        paddingBottom: open && children ? 6 : 0,
                    }}
                >
                    <div
                        style={{
                            display: 'inline-flex',
                            flexGrow: 1,
                            alignItems: 'center',
                        }}
                    >
                        {isString(label) ?
                            <Text
                                size={'sm'}
                                c={'var(--mantine-text-secondary-color)'}
                            >
                                {label}
                            </Text>
                        :   label}
                        <ActionIcon
                            size={'xs'}
                            onClick={() => {
                                setOpen(!open);
                                if (handleExpansionChange) {
                                    handleExpansionChange(!open);
                                }
                            }}
                            variant={'transparent'}
                            color={'var(--mantine-color-base-button)'}
                        >
                            {open ?
                                <IconChevronDown style={{ minWidth: 16 }} />
                            :   <IconChevronRight style={{ minWidth: 16 }} />}
                        </ActionIcon>
                    </div>
                    <Group
                        justify="flex-end"
                        gap={6}
                        style={{ display: 'inline-flex', alignItems: 'center' }}
                    >
                        {getEditButton()}
                        {getAddButton()}
                        {getDeleteButton()}
                        {getMoreButton()}
                        {labelBarNode}
                    </Group>
                </div>
                <Collapse in={open}>{children}</Collapse>
            </Card.Section>
        </Card>
    );
};
